import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { styled, useTheme } from "@mui/material/styles";

import {
  Add,
  Search,
  Refresh,
  Close,
  FilterList,
  Visibility,
  Payment,
  ReceiptLong,
  History,
  CalendarMonth,
  Print,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Select,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";
import { usePdfSettings } from "../../layouts/PdfSettingsContext.js";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
   Used everywhere a scrollable area appears: the table
   container and all modal scroll areas.
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   AUTH TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) {
      return localToken;
    }

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) {
      return sessionToken;
    }
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   CONSTANTS
========================================================= */

const months = [
  { value: 1, label: "January", short: "Jan" },
  { value: 2, label: "February", short: "Feb" },
  { value: 3, label: "March", short: "Mar" },
  { value: 4, label: "April", short: "Apr" },
  { value: 5, label: "May", short: "May" },
  { value: 6, label: "June", short: "Jun" },
  { value: 7, label: "July", short: "Jul" },
  { value: 8, label: "August", short: "Aug" },
  { value: 9, label: "September", short: "Sep" },
  { value: 10, label: "October", short: "Oct" },
  { value: 11, label: "November", short: "Nov" },
  { value: 12, label: "December", short: "Dec" },
];

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const sessionUser = sessionStorage.getItem("user");

    if (sessionUser) {
      return JSON.parse(sessionUser);
    }

    const localUser = localStorage.getItem("user");

    if (localUser) {
      return JSON.parse(localUser);
    }
  } catch (error) {
    console.error("Unable to parse logged-in user:", error);
  }

  return null;
};

/* =========================================================
   RESPONSE HELPERS
========================================================= */

const getArrayFromResponse = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.payrolls)) {
    return data.payrolls;
  }

  if (Array.isArray(data?.employees)) {
    return data.employees;
  }

  return [];
};

const getObjectFromResponse = (response) => {
  const data = response?.data;

  if (!data) {
    return null;
  }

  if (data.data && typeof data.data === "object") {
    return data.data;
  }

  return data;
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || "Something went wrong. Please try again.";
  }

  if (data.errors && typeof data.errors === "object") {
    const messages = Object.entries(data.errors).flatMap(([field, value]) => {
      const fieldName = field
        .replaceAll("_", " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

      if (Array.isArray(value)) {
        return value.map((message) => `${fieldName}: ${message}`);
      }

      return [`${fieldName}: ${value}`];
    });

    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  return (
    data.message ||
    data.detail ||
    data.error ||
    "Something went wrong. Please try again."
  );
};

/* =========================================================
   VALUE HELPERS
========================================================= */

const getEmployeeId = (item) => {
  if (!item) {
    return "";
  }

  if (typeof item.employee === "number") {
    return item.employee;
  }

  if (typeof item.employee === "string") {
    return item.employee;
  }

  return item.employee?.id || item.employee_id || "";
};

const getEmployeeName = (item) => {
  if (!item) {
    return "—";
  }

  if (item.employee && typeof item.employee === "object") {
    return (
      item.employee?.name ||
      item.employee?.employee_name ||
      item.employee?.full_name ||
      "—"
    );
  }

  return item.employee_name || item.name || "—";
};

const getEmployeeCode = (item) => {
  if (!item) {
    return "";
  }

  if (item.employee && typeof item.employee === "object") {
    return item.employee?.employee_code || item.employee?.code || "";
  }

  return item.employee_code || item.employee_code_name || "";
};

const getBranchId = (item) => {
  if (!item) {
    return "";
  }

  if (typeof item.branch === "number") {
    return item.branch;
  }

  if (typeof item.branch === "string") {
    return item.branch;
  }

  return item.branch?.id || item.branch_id || "";
};

const getBranchName = (item) => {
  if (!item) {
    return "—";
  }

  if (item.branch && typeof item.branch === "object") {
    return item.branch?.name || item.branch?.branch_name || "—";
  }

  return item.branch_name || item.branch_name_display || "—";
};

const getMonthNumber = (item) => {
  const value = item?.month || item?.payroll_month || "";

  const numericValue = Number(value);

  if (Number.isFinite(numericValue) && numericValue >= 1 && numericValue <= 12) {
    return numericValue;
  }

  return "";
};

const getMonthName = (item) => {
  const monthNumber = getMonthNumber(item);

  const month = months.find((monthItem) => monthItem.value === monthNumber);

  return month?.short || item?.month_name || "—";
};

const getPaymentStatus = (item) => {
  return item?.payment_status || item?.status || "pending";
};

const formatMoney = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* =========================================================
   BASIC SALARY VALUE HELPER
   Payroll API returns salary_structure ID instead of
   basic_salary. Resolve the ID against salary structures.
========================================================= */

const getBasicSalaryValue = (item, salaryStructures = []) => {
  if (!item) {
    return 0;
  }

  const directBasicSalary = Number(item?.basic_salary);

  if (Number.isFinite(directBasicSalary) && directBasicSalary >= 0) {
    return directBasicSalary;
  }

  const salaryStructureId = Number(item?.salary_structure);

  if (!Number.isFinite(salaryStructureId)) {
    return 0;
  }

  const salaryStructure = salaryStructures.find(
    (structure) => Number(structure?.id) === salaryStructureId
  );

  const basicSalary = Number(salaryStructure?.basic_salary);

  return Number.isFinite(basicSalary) && basicSalary >= 0 ? basicSalary : 0;
};

/* =========================================================
   DEDUCTION VALUE HELPER
   Prefer a positive value from the available deduction fields.
========================================================= */

const getDeductionValue = (item) => {
  if (!item) {
    return 0;
  }

  const candidates = [
    item?.deduction_amount,
    item?.total_deduction,
    item?.deduction,
    item?.total_deductions,
    item?.attendance_deduction,
  ];

  for (const value of candidates) {
    const parsed = Number(value);

    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return 0;
};

/* =========================================================
   COLOR HELPERS (used by the printable salary slip)
========================================================= */

const hexToRgbArray = (hex) => {
  const clean = String(hex || "#000000").replace("#", "");
  const full =
    clean.length === 3
      ? clean.split("").map((c) => c + c).join("")
      : clean;

  return [0, 2, 4].map((i) => parseInt(full.substring(i, i + 2), 16));
};

const rgbToHex = (rgb) =>
  "#" +
  rgb
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("");

// amount 0..1 → moves the color toward white
const tintColor = (hex, amount) =>
  rgbToHex(hexToRgbArray(hex).map((v) => v + (255 - v) * amount));

const DEFAULT_SLIP_PRIMARY = "#1A237E";
const DEFAULT_SLIP_ACCENT = "#FFFFFF";

/* =========================================================
   BRANCH LOGO HELPERS
========================================================= */

const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

const getAbsoluteImageUrl = (value) => {
  if (!value || typeof value !== "string") return "";

  const image = value.trim();
  if (!image) return "";

  if (/^(data:image\/|blob:|https?:\/\/)/i.test(image)) return image;
  if (image.startsWith("//")) return `https:${image}`;

  return image.startsWith("/")
    ? `${API_ORIGIN}${image}`
    : `${API_ORIGIN}/${image}`;
};

const imageUrlToDataUrl = async (imageUrl) => {
  try {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("data:image/")) return imageUrl;

    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error(`Failed: ${response.status}`);

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Unable to convert logo to data URL:", error);
    return null;
  }
};

/* =========================================================
   COMPONENT
========================================================= */

function Payroll() {
  const theme = useTheme();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  const currentYear = new Date().getFullYear();

  const currentMonth = new Date().getMonth() + 1;

  /* =======================================================
     USER / ORGANIZATION
  ======================================================= */

  const [organizationId, setOrganizationId] = useState("");

  /* =======================================================
     DATA
  ======================================================= */

  const [payrolls, setPayrolls] = useState([]);

  const [employees, setEmployees] = useState([]);

  const [salaryStructures, setSalaryStructures] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [employeeLoading, setEmployeeLoading] = useState(false);

  const [generating, setGenerating] = useState(false);

  const [detailLoading, setDetailLoading] = useState(false);

  const [salarySlipLoading, setSalarySlipLoading] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);

  const [summaryLoading, setSummaryLoading] = useState(false);

  const [markingPaid, setMarkingPaid] = useState(false);

  /* =======================================================
     SEARCH / PAGINATION
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     FILTERS (branch comes from the global Branch context)
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [filterData, setFilterData] = useState({
    employee: "",
    month: "",
    year: currentYear,
    payment_status: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    employee: "",
    month: "",
    year: currentYear,
    payment_status: "",
  });

  /* =======================================================
     GENERATE
  ======================================================= */

  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  const [generateData, setGenerateData] = useState({
    month: currentMonth,
    year: currentYear,
  });

  /* =======================================================
     DETAILS
  ======================================================= */

  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [selectedPayroll, setSelectedPayroll] = useState(null);

  const [detailData, setDetailData] = useState(null);

  /* =======================================================
     SALARY SLIP
  ======================================================= */

  const [salarySlipModalOpen, setSalarySlipModalOpen] = useState(false);

  const [salarySlipData, setSalarySlipData] = useState(null);
  const [slipColorModalOpen, setSlipColorModalOpen] = useState(false);

const [slipColorPrimary, setSlipColorPrimary] = useState(
  pdfThemeColor || DEFAULT_SLIP_PRIMARY
);

const [slipColorText, setSlipColorText] = useState(pdfTextColor);

const [slipColorAccent, setSlipColorAccent] = useState(
  DEFAULT_SLIP_ACCENT
);

const [slipBranchLogo, setSlipBranchLogo] = useState("");

useEffect(() => {
  if (slipColorModalOpen) {
    setSlipColorPrimary(pdfThemeColor || DEFAULT_SLIP_PRIMARY);
    setSlipColorText(pdfTextColor);
  }
}, [slipColorModalOpen, pdfThemeColor, pdfTextColor]);

  /* =======================================================
     PAYMENT
  ======================================================= */

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [paidDate, setPaidDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  /* =======================================================
     HISTORY
  ======================================================= */

  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [historyData, setHistoryData] = useState(null);

  const [historyEmployee, setHistoryEmployee] = useState(null);

  const [historyYear, setHistoryYear] = useState(currentYear);

  /* =======================================================
     SUMMARY
  ======================================================= */

  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  const [summaryData, setSummaryData] = useState(null);

  /* =======================================================
     ATTENDANCE
  ======================================================= */

  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);

  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const [attendanceEmployee, setAttendanceEmployee] = useState(null);

  const [attendanceMonth, setAttendanceMonth] = useState(currentMonth);

  const [attendanceYear, setAttendanceYear] = useState(currentYear);

  const [employeeAttendanceSummary, setEmployeeAttendanceSummary] =
    useState(null);

  const [employeeAttendanceHistory, setEmployeeAttendanceHistory] = useState(
    []
  );

  const [filteredAttendanceHistory, setFilteredAttendanceHistory] = useState(
    []
  );

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     STYLES
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${borderColor}`,
    borderRadius: 3,
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: inputBackground,

      "& fieldset": {
        borderColor,
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },

    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.8,
    },

    "& .MuiInputLabel-root": {
      color: textPrimary,
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  const selectStyles = {
    backgroundColor: inputBackground,
    color: textPrimary,

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "& .MuiSvgIcon-root": {
      color: textPrimary,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${borderColor}`,

        "& .MuiMenuItem-root": {
          color: textPrimary,

          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },

          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
          },
        },
      },
    },
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /* =======================================================
     LOAD AUTH USER
  ======================================================= */

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      showSnackbar("Logged-in user information not found.", "error");
      return;
    }

    const orgId =
      user?.organization_id ||
      user?.organizationId ||
      user?.organization?.id ||
      (typeof user?.organization === "number" ? user.organization : "");

    setOrganizationId(orgId || "");
  }, [showSnackbar]);

  /* =======================================================
     FETCH EMPLOYEES (scoped to global branch)
  ======================================================= */

  const fetchEmployees = useCallback(
    async (branchId = "") => {
      if (!organizationId) {
        return [];
      }

      try {
        setEmployeeLoading(true);

        const params = {
          organization_id: organizationId,
        };

        const effectiveBranch = branchId || selectedBranchId;

        if (effectiveBranch) {
          params.branch_id = effectiveBranch;
        }

        const response = await api.get("/staff/employees/", {
          params,
        });

        const data = getArrayFromResponse(response);

        setEmployees(data);

        return data;
      } catch (error) {
        console.error("Employee API Error:", error);

        setEmployees([]);

        showSnackbar(getApiErrorMessage(error), "error");

        return [];
      } finally {
        setEmployeeLoading(false);
      }
    },
    [organizationId, selectedBranchId, showSnackbar]
  );

  /* =======================================================
     FETCH SALARY STRUCTURES
  ======================================================= */

  const fetchSalaryStructures = useCallback(async () => {
    if (!organizationId) {
      setSalaryStructures([]);
      return [];
    }

    try {
      const params = {
        organization: organizationId,
      };

      if (selectedBranchId) {
        params.branch = selectedBranchId;
      }

      const response = await api.get("/hr/salary-structures/", {
        params,
      });

      const data = getArrayFromResponse(response);

      setSalaryStructures(data);

      return data;
    } catch (error) {
      console.error("Salary Structure API Error:", error);

      setSalaryStructures([]);

      showSnackbar(getApiErrorMessage(error), "error");

      return [];
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     FETCH PAYROLL
     GET /hr/payroll/
     params: organization, branch, employee, month,
             year, payment_status
  ======================================================= */

  const fetchPayrolls = useCallback(
    async (filters = appliedFilters) => {
      if (!organizationId) {
        return;
      }

      try {
        setLoading(true);

        const params = {
          organization: organizationId,
        };

        if (selectedBranchId) {
          params.branch = selectedBranchId;
        }

        if (filters?.employee) {
          params.employee = filters.employee;
        }

        if (filters?.month) {
          params.month = filters.month;
        }

        if (filters?.year) {
          params.year = filters.year;
        }

        if (filters?.payment_status) {
          params.payment_status = filters.payment_status;
        }

        const response = await api.get("/hr/payroll/", {
          params,
        });

        const data =
          response?.data?.data ||
          response?.data?.results ||
          response?.data?.payrolls ||
          response?.data ||
          [];

        setPayrolls(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Payroll API Error:", error);

        setPayrolls([]);

        showSnackbar(getApiErrorMessage(error), "error");
      } finally {
        setLoading(false);
      }
    },
    [organizationId, selectedBranchId, appliedFilters, showSnackbar]
  );

  /* =======================================================
     INITIAL DATA LOAD (runs whenever the global branch
     changes as well)
  ======================================================= */

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    const loadData = async () => {
      await fetchEmployees();

      await fetchSalaryStructures();

      await fetchPayrolls(appliedFilters);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     SEARCH FILTER (client-side; list endpoint has no
     search parameter in the Swagger spec)
  ======================================================= */

  const filteredPayrolls = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) {
      return payrolls;
    }

    return payrolls.filter((item) => {
      const employeeName = String(getEmployeeName(item)).toLowerCase();

      const employeeCode = String(getEmployeeCode(item)).toLowerCase();

      const branchName = String(getBranchName(item)).toLowerCase();

      const month = String(getMonthName(item)).toLowerCase();

      const year = String(item?.year || "").toLowerCase();

      const status = String(getPaymentStatus(item)).toLowerCase();

      const payrollId = String(item?.id || "").toLowerCase();

      return (
        employeeName.includes(search) ||
        employeeCode.includes(search) ||
        branchName.includes(search) ||
        month.includes(search) ||
        year.includes(search) ||
        status.includes(search) ||
        payrollId.includes(search)
      );
    });
  }, [payrolls, searchText]);

  const displayedPayrolls = filteredPayrolls.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     STATUS CHIP
  ======================================================= */

  const getStatusChip = (status) => {
    const value = String(status || "").toLowerCase();

    if (value === "paid") {
      return <Chip label="Paid" color="success" size="small" />;
    }

    if (value === "pending") {
      return <Chip label="Pending" color="warning" size="small" />;
    }

    return <Chip label={status || "—"} size="small" />;
  };

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

  const handleOpenFilter = () => {
    setFilterData({
      ...appliedFilters,
    });

    setFilterModalOpen(true);
  };

  const handleApplyFilters = async () => {
    const nextFilters = {
      employee: filterData.employee || "",
      month: filterData.month || "",
      year: filterData.year || "",
      payment_status: filterData.payment_status || "",
    };

    setAppliedFilters(nextFilters);

    setPage(0);

    setFilterModalOpen(false);

    await fetchPayrolls(nextFilters);
  };

  const handleClearFilters = async () => {
    const clearedFilters = {
      employee: "",
      month: "",
      year: currentYear,
      payment_status: "",
    };

    setFilterData(clearedFilters);

    setAppliedFilters(clearedFilters);

    setSearchText("");

    setPage(0);

    setFilterModalOpen(false);

    await fetchPayrolls(clearedFilters);
  };

  /* =======================================================
     REFRESH
  ======================================================= */

  const handleRefresh = async () => {
    await Promise.all([
      fetchEmployees(),
      fetchSalaryStructures(),
      fetchPayrolls(appliedFilters),
    ]);
  };

  /* =======================================================
     GENERATE MODAL
     POST /hr/payroll/generate/
     body: { organization, branch, month, year }
  ======================================================= */

  const handleOpenGenerate = () => {
    setGenerateData({
      month: currentMonth,
      year: currentYear,
    });

    setGenerateModalOpen(true);
  };

  const handleGeneratePayroll = async () => {
    if (!organizationId) {
      showSnackbar("Organization not found from logged-in user.", "error");
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        "Please select a branch from the global branch selector.",
        "error"
      );
      return;
    }

    if (!generateData.month) {
      showSnackbar("Please select a month.", "error");
      return;
    }

    if (!generateData.year) {
      showSnackbar("Please select a year.", "error");
      return;
    }

    try {
      setGenerating(true);

      await api.post("/hr/payroll/generate/", {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        month: Number(generateData.month),
        year: Number(generateData.year),
      });

      showSnackbar("Payroll generated successfully.", "success");

      setGenerateModalOpen(false);

      await fetchPayrolls(appliedFilters);
    } catch (error) {
      console.error("Generate Payroll Error:", error);

      showSnackbar(getApiErrorMessage(error), "error");
    } finally {
      setGenerating(false);
    }
  };

  /* =======================================================
     DETAILS
     GET /hr/payroll/{id}/
  ======================================================= */

  const handleViewDetails = async (item) => {
    if (!item?.id) {
      return;
    }

    setSelectedPayroll(item);

    setDetailModalOpen(true);

    setDetailLoading(true);

    try {
      const response = await api.get(`/hr/payroll/${item.id}/`);

      setDetailData(getObjectFromResponse(response));
    } catch (error) {
      console.error("Payroll Detail Error:", error);

      showSnackbar(getApiErrorMessage(error), "error");
    } finally {
      setDetailLoading(false);
    }
  };

  /* =======================================================
     SALARY SLIP
     GET /hr/payroll/{id}/salary-slip/
     Response: { success, message, salary_slip: {...} }
  ======================================================= */

  const fetchBranchLogo = async (branchId) => {
  if (!branchId) {
    setSlipBranchLogo("");
    return;
  }

  try {
    const response = await api.get(`/master/branch/${branchId}/`);
    const data = getObjectFromResponse(response);

    const image =
      data?.image ||
      data?.logo ||
      data?.branch_logo ||
      data?.branchLogo ||
      "";

    const absoluteUrl = getAbsoluteImageUrl(image);

    if (!absoluteUrl) {
      setSlipBranchLogo("");
      return;
    }

    // Embed as data URL so it prints reliably; fall back to the plain URL
    const dataUrl = await imageUrlToDataUrl(absoluteUrl);
    setSlipBranchLogo(dataUrl || absoluteUrl);
  } catch (error) {
    console.error("Branch Logo Error:", error);
    setSlipBranchLogo("");
  }
};

  const handleViewSalarySlip = async (item) => {
    if (!item?.id) {
      return;
    }

    setSelectedPayroll(item);

    setSalarySlipModalOpen(true);

setSalarySlipLoading(true);

setSlipBranchLogo("");

fetchBranchLogo(getBranchId(item) || selectedBranchId);

    try {
      const response = await api.get(`/hr/payroll/${item.id}/salary-slip/`);

      setSalarySlipData(response?.data?.salary_slip || null);
    } catch (error) {
      console.error("Salary Slip Error:", error);

      showSnackbar(getApiErrorMessage(error), "error");
    } finally {
      setSalarySlipLoading(false);
    }
  };

  /* =======================================================
     AMOUNT IN WORDS (Indian system) — used on the printed
     salary slip
  ======================================================= */

  const numberToWords = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return "";
    }

    const integerPart = Math.floor(Math.abs(number));

    if (integerPart === 0) {
      return "Zero Rupees Only";
    }

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const twoDigits = (n) => {
      if (n < 20) {
        return ones[n];
      }

      return `${tens[Math.floor(n / 10)]}${
        n % 10 ? ` ${ones[n % 10]}` : ""
      }`;
    };

    const threeDigits = (n) => {
      const hundreds = Math.floor(n / 100);

      const rest = n % 100;

      let result = "";

      if (hundreds > 0) {
        result += `${ones[hundreds]} Hundred`;
      }

      if (rest > 0) {
        result += `${hundreds > 0 ? " " : ""}${twoDigits(rest)}`;
      }

      return result;
    };

    let remaining = integerPart;

    let words = "";

    const crores = Math.floor(remaining / 10000000);

    remaining %= 10000000;

    const lakhs = Math.floor(remaining / 100000);

    remaining %= 100000;

    const thousands = Math.floor(remaining / 1000);

    remaining %= 1000;

    if (crores > 0) {
      words += `${threeDigits(crores)} Crore `;
    }

    if (lakhs > 0) {
      words += `${threeDigits(lakhs)} Lakh `;
    }

    if (thousands > 0) {
      words += `${threeDigits(thousands)} Thousand `;
    }

    if (remaining > 0) {
      words += `${threeDigits(remaining)}`;
    }

    return `${words.trim()} Rupees Only`;
  };

  /* =======================================================
     PRINT SALARY SLIP
     Opens a print window with a premium, professional
     A4 salary slip built from the verified salary_slip
     response shape.
  ======================================================= */

  const handlePrintSalarySlip = () => {
    if (!salarySlipData) {
      showSnackbar("Salary slip data not available.", "error");
      return;
    }

    const slip = salarySlipData;

    const monthLabel =
      months.find((month) => month.value === Number(slip?.month))?.label ||
      slip?.month ||
      "";

    const earningsRows = [
      ["Basic Salary", slip?.earnings?.basic_salary],
      ["HRA", slip?.earnings?.hra],
      ["DA", slip?.earnings?.da],
      ["Medical Allowance", slip?.earnings?.medical_allowance],
      ["Travel Allowance", slip?.earnings?.travel_allowance],
      ["Other Allowance", slip?.earnings?.other_allowance],
    ];

    const deductionRows = [
      ["Provident Fund (PF)", slip?.deductions?.pf],
      ["ESI", slip?.deductions?.esi],
      ["Professional Tax", slip?.deductions?.professional_tax],
      ["Other Deduction", slip?.deductions?.other_deduction],
      ["Fixed Deduction", slip?.deductions?.fixed_deduction],
      ["Attendance Deduction", slip?.deductions?.attendance_deduction],
    ];

    const isPaid = String(slip?.payment_status || "").toLowerCase() === "paid";

    const primary = slipColorPrimary;
const accent = slipColorAccent;
    const textColor = slipColorText;
    const primaryEnd = tintColor(primary, 0.2);
const primaryBorderLight = tintColor(primary, 0.75);
const primaryTint = tintColor(primary, 0.9);
const accentTint = tintColor(accent, 0.8);
const onPrimary = theme.palette.getContrastText(primary);

const logoHtml = slipBranchLogo
  ? `<img class="logo" src="${slipBranchLogo}" alt="Logo" />`
  : "";

    const infoRow = (label, value) => `
      <tr>
        <td class="info-label">${label}</td>
        <td class="info-value">${value ?? "—"}</td>
      </tr>`;

    const summaryRow = (label, value) => `
      <td class="summary-cell">
        <div class="summary-label">${label}</div>
        <div class="summary-value">${value ?? 0}</div>
      </td>`;

    const amountRow = (label, value, emphasize = false) => `
      <tr class="${emphasize ? "total-row" : ""}">
        <td>${label}</td>
        <td class="amount">₹ ${formatMoney(value)}</td>
      </tr>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Salary Slip — ${slip?.employee?.name || "Employee"}</title>
   <style>
    @page {
      size: A4;
      margin: 12mm;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      font-family: "Georgia", "Times New Roman", serif;
      color: ${textColor};
      background: #fff;
      padding: 8px 4px;
    }

    .slip {
      max-width: 800px;
      margin: 0 auto;
      border: 2px solid ${primary};
      border-radius: 10px;
      overflow: hidden;
    }

    /* HEADER */

    .slip-header {
      background: linear-gradient(135deg, ${primary} 0%, ${primaryEnd} 100%);
      color: ${onPrimary};
      padding: 22px 28px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 4px solid ${accent};
    }

        .brand {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .brand .logo {
      width: 64px;
      height: 64px;
      object-fit: contain;
      background: #fff;
      border-radius: 8px;
      padding: 4px;
    }

    .slip-header .company {
      font-size: 21px;
      letter-spacing: 2.5px;
      font-weight: bold;
      text-transform: uppercase;
    }

    .slip-header .company-sub {
      font-size: 11px;
      letter-spacing: 1.5px;
      opacity: 0.85;
      margin-top: 4px;
      text-transform: uppercase;
    }

    .slip-header .slip-title {
      text-align: right;
    }

    .slip-header .slip-title h1 {
      font-size: 24px;
      letter-spacing: 4px;
      font-weight: bold;
    }

    .slip-header .slip-period {
      font-size: 13px;
      margin-top: 4px;
      opacity: 0.9;
      letter-spacing: 1px;
    }

    /* STATUS BAND */

    .status-band {
      display: flex;
      justify-content: space-between;
      padding: 8px 28px;
      font-size: 11px;
      letter-spacing: 1px;
      background: ${primaryTint};
      border-bottom: 1px solid ${primaryBorderLight};
      color: ${textColor};
      text-transform: uppercase;
    }

    .status-band .status {
      font-weight: bold;
      color: ${isPaid ? "#2e7d32" : "#e65100"};
    }

    /* BODY */

    .slip-body {
      padding: 20px 28px 8px;
    }

    .section-title {
      font-size: 11px;
      font-weight: bold;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: ${textColor};
      border-bottom: 2px solid ${accent};
      padding-bottom: 4px;
      margin-bottom: 10px;
    }

    .info-grid {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 18px;
    }

    .info-grid td {
      vertical-align: top;
      padding: 3px 0;
      font-size: 12.5px;
    }

    .info-grid table {
      width: 100%;
      border-collapse: collapse;
    }

    .info-label {
      color: ${textColor};
      width: 32%;
      padding-right: 8px !important;
    }

    .info-value {
      font-weight: bold;
      color: ${textColor};
    }

    /* ATTENDANCE STRIP */

    .attendance-strip {
      width: 100%;
      border-collapse: separate;
      border-spacing: 6px;
      margin-bottom: 18px;
    }

    .summary-cell {
      background: ${primaryTint};
      border: 1px solid ${primaryBorderLight};
      border-radius: 6px;
      text-align: center;
      padding: 8px 4px !important;
      width: 20%;
    }

    .summary-label {
      font-size: 9.5px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: ${textColor};
    }

    .summary-value {
      font-size: 15px;
      font-weight: bold;
      color: ${textColor};
      margin-top: 2px;
    }

    /* EARNINGS / DEDUCTIONS */

    .salary-columns {
      display: flex;
      gap: 14px;
      margin-bottom: 14px;
    }

    .salary-table {
      flex: 1;
      border-collapse: collapse;
      border: 1px solid ${primaryBorderLight};
    }

    .salary-table thead th {
      background: ${primary};
      color: ${onPrimary};
      font-size: 11px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      padding: 8px 10px;
      text-align: left;
    }

    .salary-table thead th.amount {
      text-align: right;
    }

    .salary-table td {
      padding: 6px 10px;
      font-size: 12.5px;
      border-bottom: 1px solid #eceff1;
    }

    .salary-table td.amount {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .salary-table .total-row td {
      background: ${accentTint};
      font-weight: bold;
      border-top: 2px solid ${accent};
      border-bottom: none;
    }

    /* NET SALARY */

    .net-band {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(135deg, ${primary} 0%, ${primaryEnd} 100%);
      color: ${onPrimary};
      padding: 12px 20px;
      border-radius: 8px;
      margin-bottom: 8px;
      border-left: 6px solid ${accent};
    }

    .net-band .net-label {
      font-size: 13px;
      letter-spacing: 2px;
      text-transform: uppercase;
      font-weight: bold;
    }

    .net-band .net-amount {
      font-size: 22px;
      font-weight: bold;
    }

    .words-line {
      font-size: 11px;
      font-style: italic;
      color: ${textColor};
      margin-bottom: 20px;
      text-align: right;
    }

    /* FOOTER */

    .slip-footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding: 26px 28px 18px;
      border-top: 1px dashed #b0b0b0;
      margin: 0 28px;
    }

    .sign-line {
      width: 180px;
      border-top: 1px solid ${textColor};
      text-align: center;
      font-size: 11px;
      padding-top: 5px;
      letter-spacing: 1px;
    }

    .generated-note {
      text-align: center;
      font-size: 9.5px;
      color: ${textColor};
      padding-bottom: 14px;
      letter-spacing: 0.5px;
    }
  </style>
</head>
<body>
  <div class="slip">
       <div class="slip-header">
      <div class="brand">
        ${logoHtml}
        <div>
          <div class="company">${slip?.organization || "Organization"}</div>
          <div class="company-sub">${slip?.branch || "Salary Slip"}</div>
        </div>
      </div>
      <div class="slip-title">
        <h1>(PAYSHEET)</h1>
        <div class="slip-period">${monthLabel} ${slip?.year || ""}</div>
      </div>
    </div>

    <div class="status-band">
      <div>Slip No: ${slip?.payroll_id ?? "—"}</div>
      <div>Branch: ${slip?.branch || "—"}</div>
      <div>Status: <span class="status">${
        String(slip?.payment_status || "—").toUpperCase()
      }</span></div>
      <div>Paid Date: ${slip?.paid_date || "—"}</div>
    </div>

    <div class="slip-body">
      <div class="section-title">Employee Details</div>

      <table class="info-grid">
        <tr>
          <td style="width: 50%; padding-right: 20px;">
            <table>
              ${infoRow("Employee Name", slip?.employee?.name)}
              ${infoRow("Employee Code", slip?.employee?.employee_code)}
              ${infoRow("Department", slip?.employee?.department)}
            </table>
          </td>
          <td style="width: 50%;">
            <table>
              ${infoRow("Designation", slip?.employee?.designation)}
              ${infoRow("Branch", slip?.branch)}
              ${infoRow("Pay Period", `${monthLabel} ${slip?.year || ""}`)}
            </table>
          </td>
        </tr>
      </table>

      <div class="section-title">Attendance Summary</div>

      <table class="attendance-strip">
        <tr>
          ${summaryRow("Working Days", slip?.attendance?.working_days)}
          ${summaryRow("Present", slip?.attendance?.present_days)}
          ${summaryRow("Half Days", slip?.attendance?.half_days)}
          ${summaryRow("Absent", slip?.attendance?.absent_days)}
          ${summaryRow("Leave", slip?.attendance?.leave_days)}
        </tr>
      </table>

      <div class="salary-columns">
        <table class="salary-table">
          <thead>
            <tr>
              <th>Earnings</th>
              <th class="amount">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${earningsRows.map(([label, value]) => amountRow(label, value)).join("")}
            ${amountRow("Gross Salary", slip?.earnings?.gross_salary, true)}
          </tbody>
        </table>

        <table class="salary-table">
          <thead>
            <tr>
              <th>Deductions</th>
              <th class="amount">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${deductionRows.map(([label, value]) => amountRow(label, value)).join("")}
            ${amountRow(
              "Total Deductions",
              slip?.deductions?.total_deductions,
              true
            )}
          </tbody>
        </table>
      </div>

      <div class="net-band">
        <div class="net-label">Net Salary Payable</div>
        <div class="net-amount">₹ ${formatMoney(slip?.net_salary)}</div>
      </div>

      <div class="words-line">
        (In words: ${numberToWords(slip?.net_salary)})
      </div>
    </div>

    <div class="slip-footer">
      <div class="sign-line">Employee Signature</div>
      <div class="sign-line">Authorized Signatory</div>
    </div>

    <div class="generated-note">
      This is a computer-generated salary slip and does not require a physical
      signature.
    </div>
  </div>
</body>
</html>`;

    const printWindow = window.open("", "_blank", "width=900,height=1000");

    if (!printWindow) {
      showSnackbar(
        "Unable to open print window. Please allow pop-ups for this site.",
        "error"
      );
      return;
    }

    printWindow.document.write(html);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 350);
  };

  /* =======================================================
     PAYMENT
     PATCH /hr/payroll/{id}/mark-paid/
     body: { paid_date: "YYYY-MM-DD" }
  ======================================================= */

  const handleOpenPayment = (item) => {
    setSelectedPayroll(item);

    setPaidDate(new Date().toISOString().split("T")[0]);

    setPaymentModalOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedPayroll?.id) {
      return;
    }

    try {
      setMarkingPaid(true);

      await api.patch(`/hr/payroll/${selectedPayroll.id}/mark-paid/`, {
        paid_date: paidDate,
      });

      setPayrolls((prev) =>
        prev.map((item) =>
          item.id === selectedPayroll.id
            ? {
                ...item,
                payment_status: "paid",
                paid_date: paidDate,
              }
            : item
        )
      );

      showSnackbar("Payroll marked as paid successfully.", "success");

      setPaymentModalOpen(false);

      setSelectedPayroll(null);
    } catch (error) {
      console.error("Mark Paid Error:", error);

      showSnackbar(getApiErrorMessage(error), "error");
    } finally {
      setMarkingPaid(false);
    }
  };

  /* =======================================================
     HISTORY
     GET /hr/payroll/employee-history/
     params: employee* , year, organization, branch
  ======================================================= */

  const fetchHistory = useCallback(
    async (employeeId, year, branchId = "") => {
      if (!employeeId) {
        return;
      }

      setHistoryLoading(true);

      try {
        const params = {
          employee: Number(employeeId),
        };

        if (year) {
          params.year = Number(year);
        }

        if (organizationId) {
          params.organization = Number(organizationId);
        }

        const effectiveBranch = branchId || selectedBranchId;

        if (effectiveBranch) {
          params.branch = Number(effectiveBranch);
        }

        const response = await api.get("/hr/payroll/employee-history/", {
          params,
        });

        setHistoryData(getObjectFromResponse(response));
      } catch (error) {
        console.error("Employee History Error:", error);

        showSnackbar(getApiErrorMessage(error), "error");
      } finally {
        setHistoryLoading(false);
      }
    },
    [organizationId, selectedBranchId, showSnackbar]
  );

  const handleViewHistory = async (item) => {
    const employeeId = getEmployeeId(item);

    if (!employeeId) {
      showSnackbar("Employee information not available.", "error");
      return;
    }

    setHistoryEmployee({
      id: employeeId,
      name: getEmployeeName(item),
      code: getEmployeeCode(item),
    });

    setHistoryYear(Number(item?.year || currentYear));

    setHistoryModalOpen(true);

    await fetchHistory(employeeId, item?.year || currentYear, getBranchId(item));
  };

  const historyRows = useMemo(() => {
    if (!historyData) {
      return [];
    }

    if (Array.isArray(historyData)) {
      return historyData;
    }

    if (Array.isArray(historyData.history)) {
      return historyData.history;
    }

    if (Array.isArray(historyData.payrolls)) {
      return historyData.payrolls;
    }

    if (Array.isArray(historyData.data)) {
      return historyData.data;
    }

    return [];
  }, [historyData]);

  const getHistorySummaryValue = (keys) => {
    const source =
      historyData?.yearly_summary ||
      historyData?.summary ||
      historyData ||
      {};

    for (const key of keys) {
      const value = source?.[key];

      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return 0;
  };

  /* =======================================================
     SUMMARY
     GET /hr/payroll/summary/
     params: organization*, branch*, month*, year*
     (all required — branch comes from the global selector)
  ======================================================= */

  const handleOpenSummary = async () => {
    if (!organizationId) {
      showSnackbar("Organization not found.", "error");
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        "Please select a branch from the global branch selector before opening monthly summary.",
        "error"
      );
      return;
    }

    setSummaryModalOpen(true);

    setSummaryLoading(true);

    try {
      const response = await api.get("/hr/payroll/summary/", {
        params: {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
          month: Number(appliedFilters.month || currentMonth),
          year: Number(appliedFilters.year || currentYear),
        },
      });

      setSummaryData(getObjectFromResponse(response));
    } catch (error) {
      console.error("Payroll Summary Error:", error);

      showSnackbar(getApiErrorMessage(error), "error");
    } finally {
      setSummaryLoading(false);
    }
  };

  const getSummaryValue = (keys) => {
    const source = summaryData?.summary || summaryData?.data || summaryData;

    for (const key of keys) {
      const value = source?.[key];

      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return 0;
  };

  /* =======================================================
     ATTENDANCE
     GET /staff/attendance/employee/{id}/
  ======================================================= */

  const fetchAttendance = useCallback(
    async (employeeId, month, year) => {
      if (!employeeId) {
        return;
      }

      setAttendanceLoading(true);

      try {
        const response = await api.get(
          `/staff/attendance/employee/${employeeId}/`,
          {
            params: {
              month: Number(month),
              year: Number(year),
              ...(selectedBranchId
                ? { branch: Number(selectedBranchId) }
                : {}),
            },
          }
        );

        const data = getObjectFromResponse(response);

        const summary =
          data?.summary ||
          data?.attendance_summary ||
          data?.overall_summary ||
          (data && typeof data === "object" && !Array.isArray(data)
            ? data
            : null);

        setEmployeeAttendanceSummary(summary);

        const records =
          data?.records ||
          data?.attendance ||
          data?.history ||
          data?.results ||
          (Array.isArray(data) ? data : []);

        setEmployeeAttendanceHistory(records);

        setFilteredAttendanceHistory(records);

        setFromDate("");

        setToDate("");
      } catch (error) {
        console.error("Attendance API Error:", error);

        setEmployeeAttendanceSummary(null);

        setEmployeeAttendanceHistory([]);

        setFilteredAttendanceHistory([]);

        showSnackbar(getApiErrorMessage(error), "error");
      } finally {
        setAttendanceLoading(false);
      }
    },
    [selectedBranchId, showSnackbar]
  );

  const handleViewAttendance = (item) => {
    const employeeId = getEmployeeId(item);

    if (!employeeId) {
      showSnackbar("Employee information not available.", "error");
      return;
    }

    setAttendanceEmployee({
      id: employeeId,
      name: getEmployeeName(item),
      code: getEmployeeCode(item),
    });

    setAttendanceMonth(Number(getMonthNumber(item) || currentMonth));

    setAttendanceYear(Number(item?.year || currentYear));

    setAttendanceModalOpen(true);

    fetchAttendance(
      employeeId,
      getMonthNumber(item) || currentMonth,
      item?.year || currentYear
    );
  };

  const applyDateFilter = () => {
    if (!fromDate && !toDate) {
      setFilteredAttendanceHistory(employeeAttendanceHistory);
      return;
    }

    setFilteredAttendanceHistory(
      employeeAttendanceHistory.filter((row) => {
        const rowDate = row?.date;

        if (!rowDate) {
          return true;
        }

        if (fromDate && rowDate < fromDate) {
          return false;
        }

        if (toDate && rowDate > toDate) {
          return false;
        }

        return true;
      })
    );
  };

  const resetDateFilter = () => {
    setFromDate("");

    setToDate("");

    setFilteredAttendanceHistory(employeeAttendanceHistory);
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — client-side (list endpoint has no
            search param in the Swagger spec) */}

        <TextField
          size="small"
          variant="outlined"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(0);
          }}
          placeholder="Search employee, code, month, year, status..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(0);
                    }}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 380 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleOpenFilter}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* MONTHLY SUMMARY */}

          <Button
            variant="contained"
            startIcon={<ReceiptLong />}
            onClick={handleOpenSummary}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Monthly Summary
          </Button>

          {/* GENERATE PAYROLL */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenGenerate}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Generate Payroll
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          {/* MAIN TABLE — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table sx={{ minWidth: 1250 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    "& .MuiTableCell-root": {
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Employee</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Month</TableCell>

                  <TableCell>Year</TableCell>

                  <TableCell>Basic Salary</TableCell>

                  <TableCell>Gross Salary</TableCell>

                  <TableCell>Deduction</TableCell>

                  <TableCell>Net Salary</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align="center"
                    sx={{ py: 7, borderBottom: "none" }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: theme.palette.primary.main }}
                    />

                    <Typography sx={{ mt: 1.5, color: textPrimary }}>
                      Loading payroll records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedPayrolls.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align="center"
                    sx={{ py: 8, borderBottom: "none" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: textPrimary,
                      }}
                    >
                      No payroll records found
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ mt: 0.7, color: textPrimary }}
                    >
                      Generate payroll for a month to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedPayrolls.map((item, index) => (
                  <TableRow key={item.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell>
                      <Typography fontWeight={600}>
                        {getEmployeeName(item)}
                      </Typography>

                      <Typography variant="caption" color="text.primary">
                        {getEmployeeCode(item) || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>{getBranchName(item)}</TableCell>

                    <TableCell>{getMonthName(item)}</TableCell>

                    <TableCell>{item.year || "—"}</TableCell>

                    <TableCell>
                      ₹
                      {formatMoney(
                        getBasicSalaryValue(item, salaryStructures)
                      )}
                    </TableCell>

                    <TableCell>₹{formatMoney(item.gross_salary)}</TableCell>

                    <TableCell>₹{formatMoney(getDeductionValue(item))}</TableCell>

                    <TableCell>
                      <Typography fontWeight={700}>
                        ₹{formatMoney(item.net_salary)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {getStatusChip(getPaymentStatus(item))}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(item)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Salary Slip">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleViewSalarySlip(item)}
                          >
                            <ReceiptLong fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Attendance">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewAttendance(item)}
                          >
                            <CalendarMonth fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Employee History">
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => handleViewHistory(item)}
                          >
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {String(getPaymentStatus(item)).toLowerCase() !==
                          "paid" && (
                          <Tooltip title="Mark as Paid">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenPayment(item)}
                            >
                              <Payment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side */}

          <TablePagination
            component="div"
            count={filteredPayrolls.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value) || 10);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* =====================================================
          FILTER DIALOG
      ===================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Filter Payroll
          </Typography>

          <IconButton onClick={() => setFilterModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.primary">
              Branch is applied automatically from the global branch selector.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>

              <Select
                value={filterData.employee}
                label="Employee"
                onChange={(event) =>
                  setFilterData((prev) => ({
                    ...prev,
                    employee: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="">
                  <em>All Employees</em>
                </MenuItem>

                {employees.map((employee) => (
                  <MenuItem
                    key={employee?.id}
                    value={employee?.id}
                  >
                    {employee?.name ||
                      employee?.employee_name ||
                      employee?.employee_code ||
                      `Employee ${employee?.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>

              <Select
                value={filterData.month}
                label="Month"
                onChange={(event) =>
                  setFilterData((prev) => ({
                    ...prev,
                    month: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="">
                  <em>All Months</em>
                </MenuItem>

                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>

              <Select
                value={filterData.year}
                label="Year"
                onChange={(event) =>
                  setFilterData((prev) => ({
                    ...prev,
                    year: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {Array.from({ length: 7 }, (_, index) => currentYear - 3 + index).map(
                  (year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>

              <Select
                value={filterData.payment_status}
                label="Payment Status"
                onChange={(event) =>
                  setFilterData((prev) => ({
                    ...prev,
                    payment_status: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="">
                  <em>All Statuses</em>
                </MenuItem>

                <MenuItem value="pending">Pending</MenuItem>

                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClearFilters}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          GENERATE DIALOG
      ===================================================== */}

      <Dialog
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Generate Payroll
          </Typography>

          <IconButton onClick={() => setGenerateModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.primary">
              Payroll will be generated for all active employees having an
              active salary structure for the selected organization, branch,
              month and year.
            </Typography>

            <Typography variant="body2">
              Branch:{" "}
              <strong>
                {selectedBranchId
                  ? `Selected Branch (ID: ${selectedBranchId})`
                  : "No branch selected"}
              </strong>
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>

              <Select
                value={generateData.month}
                label="Month"
                onChange={(event) =>
                  setGenerateData((prev) => ({
                    ...prev,
                    month: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>

              <Select
                value={generateData.year}
                label="Year"
                onChange={(event) =>
                  setGenerateData((prev) => ({
                    ...prev,
                    year: event.target.value,
                  }))
                }
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {Array.from({ length: 7 }, (_, index) => currentYear - 3 + index).map(
                  (year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setGenerateModalOpen(false)}
            disabled={generating}
            sx={{ textTransform: "none", borderRadius: 2 }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleGeneratePayroll}
            disabled={generating}
            startIcon={
              generating ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Add />
              )
            }
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {generating ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          DETAILS DIALOG
      ===================================================== */}

      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Payroll Details
          </Typography>

          <IconButton onClick={() => setDetailModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {detailLoading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />

              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Loading payroll details...
              </Typography>
            </Box>
          ) : detailData ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                }}
              >
                <Typography fontWeight={700}>
                  {getEmployeeName(detailData)}
                </Typography>

                <Typography variant="body2" color="text.primary">
                  {getEmployeeCode(detailData) || "—"}
                </Typography>

                <Typography variant="body2" color="text.primary">
                  {getBranchName(detailData)} •{" "}
                  {getMonthName(detailData)} {detailData?.year || ""}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Basic Salary:{" "}
                  <strong>
                    ₹
                    {formatMoney(
                      getBasicSalaryValue(detailData, salaryStructures)
                    )}
                  </strong>
                </Typography>

                <Typography variant="body2" sx={{ flex: 1 }}>
                  Gross Salary:{" "}
                  <strong>₹{formatMoney(detailData?.gross_salary)}</strong>
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Deduction:{" "}
                  <strong>₹{formatMoney(getDeductionValue(detailData))}</strong>
                </Typography>

                <Typography variant="body2" sx={{ flex: 1 }}>
                  Net Salary:{" "}
                  <strong>₹{formatMoney(detailData?.net_salary)}</strong>
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Status: {getStatusChip(getPaymentStatus(detailData))}
                </Typography>

                <Typography variant="body2" sx={{ flex: 1 }}>
                  Paid Date:{" "}
                  <strong>{detailData?.paid_date || "—"}</strong>
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Alert severity="info">Payroll details not available.</Alert>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDetailModalOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SALARY SLIP DIALOG
          Built against the verified response shape:
          { success, message, salary_slip: {...} }
      ===================================================== */}

      <Dialog
        open={salarySlipModalOpen}
        onClose={() => setSalarySlipModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Salary Slip
            </Typography>

            <Typography variant="body2" color="text.primary">
              {salarySlipData
                ? `${
                    months.find((m) => m.value === salarySlipData?.month)
                      ?.label || salarySlipData?.month || ""
                  } ${salarySlipData?.year || ""}`
                : ""}
            </Typography>
          </Box>

          <IconButton onClick={() => setSalarySlipModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {salarySlipLoading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />

              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Loading salary slip...
              </Typography>
            </Box>
          ) : salarySlipData ? (
            <Stack spacing={3} sx={{ pt: 1 }}>
              {/* EMPLOYEE BLOCK */}

              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>{salarySlipData?.employee?.name || "—"}</strong>
                </Typography>

                <Typography variant="body2">
                  Code:{" "}
                  <strong>
                    {salarySlipData?.employee?.employee_code || "—"}
                  </strong>
                </Typography>

                <Typography variant="body2">
                  Department:{" "}
                  <strong>{salarySlipData?.employee?.department || "—"}</strong>
                </Typography>

                <Typography variant="body2">
                  Designation:{" "}
                  <strong>{salarySlipData?.employee?.designation || "—"}</strong>
                </Typography>

                <Typography variant="body2">
                  Organization:{" "}
                  <strong>{salarySlipData?.organization || "—"}</strong>
                </Typography>

                <Typography variant="body2">
                  Branch: <strong>{salarySlipData?.branch || "—"}</strong>
                </Typography>

                <Typography variant="body2">
                  Status: {getStatusChip(salarySlipData?.payment_status)}
                </Typography>

                <Typography variant="body2">
                  Paid Date: <strong>{salarySlipData?.paid_date || "—"}</strong>
                </Typography>
              </Box>

              {/* ATTENDANCE BLOCK */}

              <Box>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Attendance
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(5, 1fr)" },
                    gap: 1,
                  }}
                >
                  {[
                    ["Working Days", salarySlipData?.attendance?.working_days],
                    ["Present", salarySlipData?.attendance?.present_days],
                    ["Half Days", salarySlipData?.attendance?.half_days],
                    ["Absent", salarySlipData?.attendance?.absent_days],
                    ["Leave", salarySlipData?.attendance?.leave_days],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.primary">
                        {label}
                      </Typography>

                      <Typography fontWeight={700}>{value ?? 0}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* EARNINGS + DEDUCTIONS */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Earnings
                  </Typography>

                  {[
                    ["Basic Salary", salarySlipData?.earnings?.basic_salary],
                    ["HRA", salarySlipData?.earnings?.hra],
                    ["DA", salarySlipData?.earnings?.da],
                    [
                      "Medical Allowance",
                      salarySlipData?.earnings?.medical_allowance,
                    ],
                    [
                      "Travel Allowance",
                      salarySlipData?.earnings?.travel_allowance,
                    ],
                    [
                      "Other Allowance",
                      salarySlipData?.earnings?.other_allowance,
                    ],
                  ].map(([label, value]) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">{label}</Typography>

                      <Typography variant="body2" fontWeight={600}>
                        ₹{formatMoney(value)}
                      </Typography>
                    </Stack>
                  ))}

                  <Divider sx={{ my: 1 }} />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>
                      Gross Salary
                    </Typography>

                    <Typography variant="body2" fontWeight={700}>
                      ₹{formatMoney(salarySlipData?.earnings?.gross_salary)}
                    </Typography>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Deductions
                  </Typography>

                  {[
                    ["PF", salarySlipData?.deductions?.pf],
                    ["ESI", salarySlipData?.deductions?.esi],
                    [
                      "Professional Tax",
                      salarySlipData?.deductions?.professional_tax,
                    ],
                    [
                      "Other Deduction",
                      salarySlipData?.deductions?.other_deduction,
                    ],
                    [
                      "Fixed Deduction",
                      salarySlipData?.deductions?.fixed_deduction,
                    ],
                    [
                      "Attendance Deduction",
                      salarySlipData?.deductions?.attendance_deduction,
                    ],
                  ].map(([label, value]) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">{label}</Typography>

                      <Typography variant="body2" fontWeight={600}>
                        ₹{formatMoney(value)}
                      </Typography>
                    </Stack>
                  ))}

                  <Divider sx={{ my: 1 }} />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>
                      Total Deductions
                    </Typography>

                    <Typography variant="body2" fontWeight={700}>
                      ₹{formatMoney(salarySlipData?.deductions?.total_deductions)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              {/* NET SALARY */}

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.action.hover,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography fontWeight={800}>Net Salary</Typography>

                <Typography variant="h6" fontWeight={800}>
                  ₹{formatMoney(salarySlipData?.net_salary)}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Alert severity="info">Salary slip not available.</Alert>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Print />}
           onClick={() => setSlipColorModalOpen(true)}
            disabled={salarySlipLoading || !salarySlipData}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Print Salary Slip
          </Button>

          <Button
            onClick={() => setSalarySlipModalOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          PAYMENT DIALOG
      ===================================================== */}

      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Mark as Paid
          </Typography>

          <IconButton onClick={() => setPaymentModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {selectedPayroll && (
            <Box
              sx={{
                p: 2,
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography fontWeight={700}>
                {getEmployeeName(selectedPayroll)}
              </Typography>

              <Typography variant="body2" color="text.primary">
                {getEmployeeCode(selectedPayroll)}
              </Typography>

              <Typography variant="body2" sx={{ mt: 1 }}>
                Net Salary:{" "}
                <strong>₹{formatMoney(selectedPayroll.net_salary)}</strong>
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Paid Date"
            value={paidDate}
            onChange={(event) => setPaidDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={textFieldStyles}
          />
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setPaymentModalOpen(false)}
            disabled={markingPaid}
            sx={{ textTransform: "none", borderRadius: 2 }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleMarkPaid}
            disabled={markingPaid}
            startIcon={
              markingPaid ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Payment />
              )
            }
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {markingPaid ? "Updating..." : "Mark as Paid"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          HISTORY DIALOG
      ===================================================== */}

      <Dialog
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Employee Payroll History
            </Typography>

            <Typography variant="body2" color="text.primary">
              {historyEmployee?.name || "Employee"}{" "}
              {historyEmployee?.code ? `• ${historyEmployee.code}` : ""}
            </Typography>
          </Box>

          <IconButton onClick={() => setHistoryModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {/* HISTORY YEAR */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Year</InputLabel>

              <Select
                value={historyYear}
                label="Year"
                onChange={(event) => {
                  const year = Number(event.target.value);

                  setHistoryYear(year);

                  if (historyEmployee?.id) {
                    fetchHistory(historyEmployee.id, year);
                  }
                }}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                {Array.from({ length: 7 }, (_, index) => currentYear - 3 + index).map(
                  (year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {historyLoading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />

              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Loading employee history...
              </Typography>
            </Box>
          ) : (
            <>
              {/* YEARLY SUMMARY */}

              {historyData && typeof historyData === "object" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(4, 1fr)",
                    },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.primary">
                        Payroll Count
                      </Typography>

                      <Typography variant="h5" fontWeight={800}>
                        {getHistorySummaryValue([
                          "payroll_count",
                          "total_payrolls",
                          "count",
                        ])}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.primary">
                        Gross Salary
                      </Typography>

                      <Typography variant="h6" fontWeight={800}>
                        ₹
                        {formatMoney(
                          getHistorySummaryValue([
                            "gross_salary",
                            "total_gross_salary",
                          ])
                        )}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.primary">
                        Total Deduction
                      </Typography>

                      <Typography variant="h6" fontWeight={800}>
                        ₹
                        {formatMoney(
                          getDeductionValue(
                            historyData?.yearly_summary || historyData
                          )
                        )}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.primary">
                        Net Salary
                      </Typography>

                      <Typography variant="h6" fontWeight={800}>
                        ₹
                        {formatMoney(
                          getHistorySummaryValue([
                            "net_salary",
                            "total_net_salary",
                          ])
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* HISTORY TABLE */}

              <CustomTableScrollbar>
                <TableContainer
                  sx={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                    maxHeight: "45vh",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          "#",
                          "Month",
                          "Basic Salary",
                          "Gross Salary",
                          "Deduction",
                          "Net Salary",
                          "Payment Status",
                        ].map((heading) => (
                          <TableCell
                            key={heading}
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff !important",
                              fontWeight: 700,
                            }}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {historyRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                            <Typography fontWeight={600}>
                              No payroll history found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        historyRows.map((item, index) => (
                          <TableRow key={item.id || index} hover>
                            <TableCell>{index + 1}</TableCell>

                            <TableCell>
                              <Typography fontWeight={600}>
                                {getMonthName(item)}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.primary"
                              >
                                {item.year || historyYear}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              ₹
                              {formatMoney(
                                getBasicSalaryValue(item, salaryStructures)
                              )}
                            </TableCell>

                            <TableCell>
                              ₹{formatMoney(item.gross_salary)}
                            </TableCell>

                            <TableCell>
                              ₹{formatMoney(getDeductionValue(item))}
                            </TableCell>

                            <TableCell>
                              <Typography fontWeight={700}>
                                ₹{formatMoney(item.net_salary)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {getStatusChip(getPaymentStatus(item))}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CustomTableScrollbar>
            </>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setHistoryModalOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SUMMARY DIALOG
      ===================================================== */}

      <Dialog
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Monthly Payroll Summary
            </Typography>

            <Typography variant="body2" color="text.primary">
              {months.find(
                (month) =>
                  month.value ===
                  Number(appliedFilters.month || currentMonth)
              )?.label || "Current Month"}{" "}
              {appliedFilters.year || currentYear}
            </Typography>
          </Box>

          <IconButton onClick={() => setSummaryModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {summaryLoading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />

              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Loading monthly summary...
              </Typography>
            </Box>
          ) : summaryData ? (
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.primary">
                      Employees
                    </Typography>

                    <Typography variant="h4" fontWeight={800}>
                      {getSummaryValue([
                        "employee_count",
                        "employees_count",
                        "total_employees",
                      ])}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.primary">
                      Paid
                    </Typography>

                    <Typography variant="h4" fontWeight={800}>
                      {getSummaryValue([
                        "paid_count",
                        "paid_employees",
                      ])}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.primary">
                      Pending
                    </Typography>

                    <Typography variant="h4" fontWeight={800}>
                      {getSummaryValue([
                        "pending_count",
                        "pending_employees",
                      ])}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.primary">
                      Total Net Salary
                    </Typography>

                    <Typography variant="h6" fontWeight={800}>
                      ₹
                      {formatMoney(
                        getSummaryValue([
                          "net_salary",
                          "total_net_salary",
                          "net_total",
                        ])
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                Summary is based on the globally selected branch, the applied
                month and year filters.
              </Alert>
            </Box>
          ) : (
            <Alert severity="info">Summary data not available.</Alert>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSummaryModalOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          ATTENDANCE DIALOG
      ===================================================== */}

      <Dialog
        open={attendanceModalOpen}
        onClose={() => setAttendanceModalOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
            fontWeight: "bold",
            fontSize: "1.3rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Monthly Attendance – {attendanceEmployee?.name || "Employee"}
            </Typography>

            {attendanceEmployee?.code && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {attendanceEmployee.code}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={() => setAttendanceModalOpen(false)}
            sx={{ color: theme.palette.primary.contrastText }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <StyledDialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 3,
              px: 2,
              py: 2,
              mb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontSize: "15px",
              fontWeight: 600,
            }}
          >
            <Typography>
              <strong>Present:</strong>{" "}
              <Box component="span" sx={{ color: "#2e7d32" }}>
                {employeeAttendanceSummary?.present_days ?? 0}
              </Box>
            </Typography>

            <Typography>
              <strong>Absent:</strong>{" "}
              <Box component="span" sx={{ color: "#d32f2f" }}>
                {employeeAttendanceSummary?.absent_days ?? 0}
              </Box>
            </Typography>

            <Typography>
              <strong>Half Day:</strong>{" "}
              <Box component="span" sx={{ color: "#ed6c02" }}>
                {employeeAttendanceSummary?.half_days ?? 0}
              </Box>
            </Typography>

            <Typography>
              <strong>Leave:</strong>{" "}
              <Box component="span" sx={{ color: "#0288d1" }}>
                {employeeAttendanceSummary?.leave_days ?? 0}
              </Box>
            </Typography>

            <Typography>
              <strong>Working Days:</strong>{" "}
              <Box component="span" sx={{ color: "#7b1fa2" }}>
                {employeeAttendanceSummary?.working_days ?? 0}
              </Box>
            </Typography>

            <Typography>
              <strong>Attendance:</strong>{" "}
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                {employeeAttendanceSummary?.attendance_percentage ?? 0}%
              </Box>
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: theme.palette.primary.main,
              }}
            >
              Attendance Details
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 2, mt: 2, alignItems: "center", flexWrap: "wrap" }}
            >
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={textFieldStyles}
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={textFieldStyles}
              />

              <Button
                variant="contained"
                onClick={applyDateFilter}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Apply
              </Button>

              <Button
                variant="outlined"
                onClick={resetDateFilter}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Reset
              </Button>
            </Stack>

            {attendanceLoading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={32} />

                <Typography
                  variant="body2"
                  sx={{ mt: 1 }}
                  color="text.primary"
                >
                  Loading attendance...
                </Typography>
              </Box>
            ) : filteredAttendanceHistory.length === 0 ? (
              <Alert severity="info">No attendance found.</Alert>
            ) : (
              <CustomTableScrollbar>
                <TableContainer
                  sx={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                    maxHeight: "45vh",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          S.No
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Date
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Day
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Status
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Time In
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Time Out
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Total Hours
                        </TableCell>

                        <TableCell
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff !important",
                            fontWeight: 700,
                          }}
                        >
                          Remark
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredAttendanceHistory.map((row, index) => (
                        <TableRow
                          key={row?.id || `${row?.date || "date"}-${index}`}
                          hover
                        >
                          <TableCell>{index + 1}</TableCell>

                          <TableCell>{row?.date || "—"}</TableCell>

                          <TableCell>{row?.day || "—"}</TableCell>

                          <TableCell>
                            {String(row?.status || "").toLowerCase() ===
                              "present" && (
                              <Chip label="Present" color="success" size="small" />
                            )}

                            {String(row?.status || "").toLowerCase() ===
                              "absent" && (
                              <Chip label="Absent" color="error" size="small" />
                            )}

                            {String(row?.status || "").toLowerCase() ===
                              "half_day" && (
                              <Chip
                                label="Half Day"
                                color="warning"
                                size="small"
                              />
                            )}

                            {String(row?.status || "").toLowerCase() ===
                              "on_leave" && (
                              <Chip label="Leave" color="info" size="small" />
                            )}

                            {!row?.status && "—"}
                          </TableCell>

                          <TableCell>{row?.time_in || "N/A"}</TableCell>

                          <TableCell>{row?.time_out || "N/A"}</TableCell>

                          <TableCell>{row?.total_hours || "N/A"}</TableCell>

                          <TableCell>{row?.remark || "No Remark"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CustomTableScrollbar>
            )}
          </Box>
        </StyledDialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setAttendanceModalOpen(false)}
            sx={{ minWidth: 120, textTransform: "none", borderRadius: 2 }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* =====================================================
    SALARY SLIP COLOR DIALOG
===================================================== */}

<Dialog
  open={slipColorModalOpen}
  onClose={() => setSlipColorModalOpen(false)}
  fullWidth
  maxWidth="xs"
  PaperProps={{ sx: dialogPaperStyles }}
  BackdropProps={{
    sx: {
      backgroundColor: "rgba(0, 0, 0, 0.35)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)", // Safari
    },
  }}
>
  <Box
    sx={{
      backgroundColor: slipColorPrimary,
      px: 2.5,
      py: 1.8,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      transition: "background-color 0.2s ease",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        color: theme.palette.getContrastText(slipColorPrimary),
        fontWeight: 700,
      }}
    >
      Slip Appearance
    </Typography>

    <IconButton
      onClick={() => setSlipColorModalOpen(false)}
      sx={{ color: theme.palette.getContrastText(slipColorPrimary) }}
    >
      <Close />
    </IconButton>
  </Box>

  <Box sx={{ p: 3 }}>
    {[
      {
        title: "Primary Color",
        caption: "Header, table headings, net salary band",
        value: slipColorPrimary,
        setter: setSlipColorPrimary,
      },
      {
        title: "Accent Color",
        caption: "Section underlines, highlights",
        value: slipColorAccent,
        setter: setSlipColorAccent,
      },
    ].map((item) => (
      <Box
        key={item.title}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography fontWeight={600}>{item.title}</Typography>

          <Typography variant="caption" color="text.secondary">
            {item.caption}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="input"
            type="color"
            value={item.value}
            onChange={(e) => item.setter(e.target.value)}
            sx={{
              width: 48,
              height: 40,
              p: 0.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              cursor: "pointer",
              backgroundColor: "transparent",
            }}
          />

          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", minWidth: 75 }}
          >
            {item.value.toUpperCase()}
          </Typography>
        </Box>
      </Box>
    ))}

    {/* PREVIEW */}

    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
      Preview
    </Typography>

    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        mb: 3,
      }}
    >
    <Box
  sx={{
    backgroundColor: slipColorPrimary,
    color: theme.palette.getContrastText(slipColorPrimary),
    borderBottom: `4px solid ${slipColorAccent}`,
    px: 2,
    py: 1.2,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    transition: "all 0.2s ease",
  }}
>
  {slipBranchLogo ? (
    <Box
      component="img"
      src={slipBranchLogo}
      alt="Branch Logo"
      sx={{
        width: 36,
        height: 36,
        objectFit: "contain",
        backgroundColor: "#fff",
        borderRadius: 1,
        p: 0.3,
      }}
    />
  ) : (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1,
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 8,
        fontWeight: 700,
        color: slipColorPrimary,
      }}
    >
      LOGO
    </Box>
  )}

  <Box>
    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
      {salarySlipData?.organization || "Organization"}
    </Typography>

    <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
      {salarySlipData?.branch || "Salary Slip"}
    </Typography>
  </Box>
</Box>

      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            letterSpacing: 1.5,
            color: slipColorPrimary,
            borderBottom: `2px solid ${slipColorAccent}`,
            pb: 0.4,
            mb: 1,
          }}
        >
          EARNINGS
        </Typography>

        <Box
          sx={{
            height: 7,
            width: "70%",
            borderRadius: 1,
            backgroundColor: "divider",
            mb: 0.8,
          }}
        />

        <Box
          sx={{
            height: 7,
            width: "50%",
            borderRadius: 1,
            backgroundColor: "divider",
          }}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: slipColorPrimary,
          color: theme.palette.getContrastText(slipColorPrimary),
          borderLeft: `6px solid ${slipColorAccent}`,
          px: 2,
          py: 0.8,
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: 1.5,
        }}
      >
        NET SALARY PAYABLE
      </Box>
    </Box>

    {/* ACTIONS */}

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Button
        variant="text"
        onClick={() => {
          setSlipColorPrimary(pdfThemeColor || DEFAULT_SLIP_PRIMARY);
          setSlipColorText(pdfTextColor);
          setSlipColorAccent(DEFAULT_SLIP_ACCENT);
        }}
        sx={{ textTransform: "none" }}
      >
        Reset Colors
      </Button>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={() => setSlipColorModalOpen(false)}
          sx={{ textTransform: "none" }}
         data-app-cancel="true">
          Cancel
        </Button>

        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => {
            setSlipColorModalOpen(false);
            handlePrintSalarySlip();
          }}
          sx={{ textTransform: "none" }}
        >
          Print
        </Button>
      </Box>
    </Box>
  </Box>
</Dialog>

    </Box>
  );
}

export default Payroll;